import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { useFeedbackAlerts } from "../_app/hooks";
import { genericError } from "../_app/utils/text";
import {
  assignCostCentres,
  createCostCentre,
  deleteCostCentre,
  getAccountHierarchy,
  getAccounts,
  getBillingAddress,
  getBillSettings,
  getCostCentres,
  getFilteredCostCentres,
  getMyAccounts,
  getMyCostCentres,
  updateBillingAddress,
  updateBillSettings,
  updateCostCentre,
} from "./api";
import { costCentreFilters } from "./filterConfig";
import {
  AccessibleAccountDto,
  BillingAddressUpdateParams,
  BillSettings,
  CostCentre,
  GetAccountsParams,
  GetAccountsResponse,
} from "./types";
import { AccountLevel } from "./utils";
import { UserType } from "../user-level/types";
import { useContextProvider } from "../context/providers/ContextProvider";
import { useMyUserLevelType } from "../user-level/hooks";
import { cloneDeep } from "../_app/utils";

export function useUserAccessibleAccounts(): AccessibleAccountDto[] {
  const { data: groups } = useMyAccounts();
  const { selectedGroupId } = useContextProvider();
  return groups?.find((group) => group.id === selectedGroupId)?.userAccessibleAccounts ?? [];
}

export function useBillingAddress(options = {}) {
  const { contextId } = useContextProvider();
  const accountId = contextId;

  return useQuery({
    queryKey: ["billingAddress", accountId],
    queryFn: () => getBillingAddress(accountId),
    enabled: !!accountId,
    ...options,
  });
}

export function useBillingAddressUpdate(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId } = useContextProvider();
  const accountId = contextId;

  return useMutation({
    mutationFn: (address: BillingAddressUpdateParams) => updateBillingAddress(address, accountId),

    onSuccess: (data) => {
      queryClient.setQueriesData({ queryKey: ["billingAddress"] }, () => data);
      setFeedbackAlertSuccess("Billing address changed.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    ...options,
  });
}

export function useBillSettings(options = {}) {
  const { contextId } = useContextProvider();
  const accountId = contextId;

  return useQuery({
    queryKey: ["billSettings"],
    queryFn: () => getBillSettings(accountId),
    enabled: !!accountId,
    ...options,
  });
}

export function useBillSettingsUpdate(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId } = useContextProvider();
  const accountId = contextId;

  return useMutation({
    mutationFn: (billSettings: BillSettings) => updateBillSettings(billSettings, accountId),

    onSuccess: (response, body: BillSettings) => {
      queryClient.setQueriesData({ queryKey: ["billSettings"] }, () => body);
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },

    ...options,
  });
}

export function usePaperBillCharge() {
  const unit = "£";
  const value = "7.50";
  const parsed = `${unit}${value}`;
  return { unit, value, parsed };
}

export function useMyAccounts(options = {}) {
  const { data: userType } = useMyUserLevelType();
  return useQuery({
    queryKey: ["myAccounts", { type: userType }],
    queryFn: getMyAccounts,
    enabled: userType === UserType.ACCOUNT,
    ...options,
  });
}

export function useAccountsForParent(params: GetAccountsParams, options = {}) {
  return useInfiniteQuery<GetAccountsResponse>({
    queryKey: ["accountsForParent", JSON?.stringify(params || {})],
    queryFn: ({ pageParam }) => {
      return getAccounts({
        tree: true,
        offset: pageParam as number,
        ...params,
      });
    },
    initialPageParam: 0,
    getNextPageParam: ({ page, pageSize }: any) => (page + 1) * pageSize,
    ...options,
  });
}

export function useAccountHierarchy(accountId: string | undefined, options = {}) {
  const { data: userType } = useMyUserLevelType();
  return useQuery({
    queryKey: ["accountHierarchy", accountId],
    queryFn: () => getAccountHierarchy(accountId),

    select: (data) => ({
      level: AccountLevel.fromString(data.level as any)!,
      hierarchy: data.hierarchy,
    }),

    enabled: Boolean(userType === UserType.ACCOUNT && accountId),
    ...options,
  });
}

/* COST CENTRES START */

export function useCostCentres(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["costCentres", accountId],
    queryFn: () => getCostCentres(accountId),
    ...options,
  });
}

export function useCostCentresMy(options = {}) {
  const { data: userType } = useMyUserLevelType();
  return useQuery({
    queryKey: ["costCentres"],
    queryFn: getMyCostCentres,
    enabled: userType === UserType.COST_CENTRE,
    ...options,
  });
}

export function useFilteredCostCentres(search: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["filtered-cost-centres", search, accountId],
    queryFn: () => getFilteredCostCentres({ search, "account-id": accountId }),
    ...options,
  });
}

export function useCostCentreUpdate(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (update: CostCentre) => updateCostCentre(update),

    onSuccess: (updated: CostCentre) => {
      queryClient.setQueriesData({ queryKey: ["costCentres"] }, (list: any) => {
        const updateMatching = (old: any) => {
          old?.forEach(function (cc: any, i: number) {
            if (cc.id === updated.id) {
              //@ts-ignore
              this[i] = updated;
            } else if (cc.subCostCentres.length) {
              updateMatching(cc.subCostCentres);
            }
          }, old);
        };
        if (list) {
          const cloned = cloneDeep(list);
          updateMatching(cloned);
          return cloned;
        }
        return list;
      });
      setFeedbackAlertSuccess("Cost Centre updated.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useCostCentreCreate(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (cc: CostCentre) => createCostCentre(cc, accountId),

    onSuccess: (updated: any) => {
      queryClient.setQueriesData({ queryKey: ["costCentres"] }, (list: any) => {
        const updateMatching = (old: any) => {
          if (updated.parent === null) {
            old?.push(updated);
          } else {
            old?.forEach(function (cc: any, i: number) {
              if (cc.id === updated.parent) {
                //@ts-ignore
                this[i].subCostCentres.push(updated);
              } else if (cc.subCostCentres.length) {
                updateMatching(cc.subCostCentres);
              }
            }, old);
          }
        };
        if (list) {
          const cloned = cloneDeep(list);
          updateMatching(cloned);
          return cloned;
        }
        return list;
      });

      setFeedbackAlertSuccess("Cost Centre added");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useCostCentreDelete(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();

  return useMutation({
    mutationFn: (id: string) => deleteCostCentre(id),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["costCentres"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assets"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assets-live"],
      });
      setFeedbackAlertSuccess("Cost Centre deleted");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message);
    },

    ...options,
  });
}

export function useCostCentresAssign(
  params?: { page: number; limit: number; queries?: any[]; clis?: string[] },
  options: any = {},
  refetchCCs: boolean = true,
) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (assignParams: any) => assignCostCentres(assignParams, accountId),
    ...options,

    onSuccess: () => {
      if (typeof options?.onSuccess === "function") options?.onSuccess?.();
      if (refetchCCs)
        queryClient.invalidateQueries({
          queryKey: ["costCentres"],
        });
      queryClient.invalidateQueries({
        queryKey: ["assets-live"],
      });
      queryClient.invalidateQueries({
        queryKey: ["assets", params],
      });
      setFeedbackAlertSuccess("Cost centre updated.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || genericError());
    },
  });
}

export function useCostCentresFilters() {
  return { data: costCentreFilters };
}

/* COST CENTRES END */
